// Chat attachments (phase 2): the client-side checks that mirror the API's
// limits, and the composer's attachment state.
//
// Signed in: each picked file uploads at once (POST /chat/attachments, the
// project's folder when the chat belongs to a project); Kirim waits while any
// upload runs and sends the ready ids as attachmentIds.
// Guest: one file of at most 5 MB, read into base64 and sent inline with the
// guest message (nothing is uploaded or stored).
// A 503 drive_not_connected marks the files and raises the composer notice;
// text chat keeps working.
import { useCallback, useEffect, useRef, useState } from "react";
import * as API from "../api";
import type { AttachmentRef } from "../api";
import type { GlyphName } from "../ui/icons";

export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const GUEST_MAX_FILE_BYTES = 5 * 1024 * 1024;
export const MAX_FILES = 5;
export const GUEST_MAX_FILES = 1;

/** Extensions the API accepts (it checks the content too). */
const TYPES: Record<string, string[]> = {
  png: ["image/png"],
  jpg: ["image/jpeg"],
  jpeg: ["image/jpeg"],
  webp: ["image/webp"],
  pdf: ["application/pdf"],
  docx: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
  xlsx: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  txt: ["text/plain"],
};

/** The file input's accept list. */
export const ACCEPT = [...Object.keys(TYPES).map((e) => `.${e}`), ...new Set(Object.values(TYPES).flat())].join(",");

export const MSG = {
  type: "Jenis file tidak didukung. Gunakan PNG, JPG, WEBP, PDF, DOCX, XLSX, atau TXT.",
  size: "Ukuran file melebihi 10 MB.",
  guestSize: "Ukuran file tamu maksimal 5 MB.",
  empty: "File kosong tidak dapat dilampirkan.",
  count: `Maksimal ${MAX_FILES} file per pesan.`,
  guestCount: "Tamu hanya dapat melampirkan 1 file per pesan.",
  drive: "Penyimpanan file belum tersedia. Hubungi admin.",
  quota: "Ruang penyimpanan file Anda sudah penuh (batas 500 MB). Hapus percakapan atau project yang tidak dipakai lagi, lalu coba lagi.",
  limit: "Terlalu banyak unggahan. Tunggu sebentar, lalu coba lagi.",
  failed: "File belum terunggah. Hapus lalu lampirkan lagi.",
  read: "File tidak dapat dibaca. Coba pilih lagi.",
} as const;

/** The default instruction when files are sent with an empty composer (the API needs text). */
export const FILES_ONLY_MESSAGE = "Tolong pelajari lampiran ini.";

export function extensionOf(name: string): string {
  const m = /\.([A-Za-z0-9]+)$/.exec(name.trim());
  return m ? m[1]!.toLowerCase() : "";
}

/** The reason a file cannot be attached, or null when it can. */
export function checkFile(file: { name: string; size: number; type?: string }, guest = false): string | null {
  const ext = extensionOf(file.name);
  const mimes = TYPES[ext];
  const mime = (file.type ?? "").split(";")[0]!.trim().toLowerCase();
  if (!mimes || (mime && mime !== "application/octet-stream" && !mimes.includes(mime))) return MSG.type;
  if (file.size <= 0) return MSG.empty;
  if (file.size > (guest ? GUEST_MAX_FILE_BYTES : MAX_FILE_BYTES)) return guest ? MSG.guestSize : MSG.size;
  return null;
}

/** "820 B", "14 KB", "2,4 MB" (Indonesian decimal comma). */
export function formatBytes(size: number): string {
  if (!Number.isFinite(size) || size < 0) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  const mb = size / (1024 * 1024);
  return `${(mb < 10 ? mb.toFixed(1) : String(Math.round(mb))).replace(".", ",")} MB`;
}

export type FileKind = "image" | "pdf" | "docx" | "xlsx" | "txt" | "other";

export function fileKind(name: string, mimeType = ""): FileKind {
  const ext = extensionOf(name);
  if (mimeType.startsWith("image/") || ["png", "jpg", "jpeg", "webp"].includes(ext)) return "image";
  if (ext === "pdf" || mimeType === "application/pdf") return "pdf";
  if (ext === "docx" || mimeType.includes("wordprocessingml")) return "docx";
  if (ext === "xlsx" || mimeType.includes("spreadsheetml")) return "xlsx";
  if (ext === "txt" || mimeType === "text/plain") return "txt";
  return "other";
}

export function kindGlyph(kind: FileKind): GlyphName {
  if (kind === "image") return "gallery";
  if (kind === "xlsx") return "element3";
  return "documentText";
}

/** A short type label for the chip's meta line. */
export function kindLabel(name: string, mimeType = ""): string {
  const ext = extensionOf(name);
  if (ext) return ext === "jpeg" ? "JPG" : ext.toUpperCase();
  return fileKind(name, mimeType) === "image" ? "Gambar" : "File";
}

/** Upload and send errors in Indonesian, by the API's status and code. */
export function uploadErrorMessage(err: unknown): string {
  if (!(err instanceof API.ApiError)) return MSG.failed;
  const code = typeof err.body?.code === "string" ? err.body.code : null;
  if (err.status === 503 || code === "drive_not_connected") return MSG.drive;
  // The server's own text names the configured quota.
  if (code === "storage_quota_exceeded") return typeof err.body?.error === "string" ? err.body.error : MSG.quota;
  if (err.status === 413 || code === "file_too_large") return MSG.size;
  if (code === "unsupported_type") return MSG.type;
  if (code === "too_many_files") return MSG.count;
  if (err.status === 429) return err.retryAfter ? `${MSG.limit.replace(" Tunggu sebentar,", ` Tunggu ${err.retryAfter} detik,`)}` : MSG.limit;
  if (err.status === 0) return API.errorMessage(err);
  return MSG.failed;
}

export function isDriveOff(err: unknown): boolean {
  return err instanceof API.ApiError && (err.status === 503 || err.body?.code === "drive_not_connected");
}

/** Standard base64 of a file's bytes (the guest's inline file). */
export async function fileToBase64(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  return btoa(binary);
}

export type ItemStatus = "uploading" | "ready" | "error";

export interface AttachmentItem {
  key: string;
  name: string;
  size: number;
  mimeType: string;
  status: ItemStatus;
  /** Why it cannot be sent (status "error"). */
  error?: string;
  /** The stored attachment (signed in, after upload). */
  ref?: AttachmentRef;
  /** The guest's inline file. */
  data?: string;
}

export interface Attachments {
  items: AttachmentItem[];
  guest: boolean;
  /** Any upload (or guest read) still running: Kirim waits. */
  pending: boolean;
  /** Files ready to go with the next message. */
  ready: AttachmentItem[];
  /** The last batch went over the count limit (shown under the chips). */
  note: string | null;
  /** Drive answered 503: the composer shows the notice, text chat still works. */
  driveOff: boolean;
  /** How many more files fit. */
  room: number;
  add: (files: File[]) => void;
  remove: (key: string) => void;
  /** After a send: empty the tray. */
  clear: () => void;
}

let keySeq = 0;
const nextKey = () => `att-${++keySeq}`;

export function useAttachments({ guest = false, projectId }: { guest?: boolean; projectId?: string } = {}): Attachments {
  const [items, setItems] = useState<AttachmentItem[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [driveOff, setDriveOff] = useState(false);
  const live = useRef(new Set<string>());
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(() => () => live.current.clear(), []);

  const max = guest ? GUEST_MAX_FILES : MAX_FILES;
  const patch = useCallback((key: string, next: Partial<AttachmentItem>) => {
    if (!live.current.has(key)) return;
    setItems((list) => list.map((it) => (it.key === key ? { ...it, ...next } : it)));
  }, []);

  const add = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      // Errored chips do not count toward the limit; the guest's one file is replaced.
      const current = itemsRef.current.filter((it) => it.status !== "error");
      let base = guest ? [] : itemsRef.current;
      let room = guest ? max : max - current.length;
      let overflow = false;
      const added: AttachmentItem[] = [];
      const toUpload: { item: AttachmentItem; file: File }[] = [];
      for (const file of files) {
        const reason = checkFile(file, guest);
        const item: AttachmentItem = { key: nextKey(), name: file.name || "lampiran", size: file.size, mimeType: file.type, status: reason ? "error" : "uploading", error: reason ?? undefined };
        if (!reason) {
          if (room <= 0) {
            overflow = true;
            continue;
          }
          room -= 1;
          toUpload.push({ item, file });
        }
        added.push(item);
      }
      if (guest && added.length > 0) {
        for (const it of itemsRef.current) live.current.delete(it.key);
        base = [];
      }
      for (const it of added) live.current.add(it.key);
      setNote(overflow ? (guest ? MSG.guestCount : MSG.count) : null);
      setItems([...base, ...added]);
      for (const { item, file } of toUpload) {
        if (guest) {
          fileToBase64(file).then(
            (data) => patch(item.key, { status: "ready", data }),
            () => patch(item.key, { status: "error", error: MSG.read }),
          );
          continue;
        }
        API.uploadAttachments([file], projectId).then(
          (refs) => {
            const ref = refs[0];
            if (ref) patch(item.key, { status: "ready", ref, mimeType: ref.mimeType || item.mimeType, size: ref.size || item.size });
            else patch(item.key, { status: "error", error: MSG.failed });
          },
          (err) => {
            if (isDriveOff(err)) setDriveOff(true);
            patch(item.key, { status: "error", error: uploadErrorMessage(err) });
          },
        );
      }
    },
    [guest, max, patch, projectId],
  );

  const remove = useCallback((key: string) => {
    live.current.delete(key);
    setItems((list) => list.filter((it) => it.key !== key));
    setNote(null);
  }, []);

  const clear = useCallback(() => {
    live.current.clear();
    setItems([]);
    setNote(null);
  }, []);

  const ready = items.filter((it) => it.status === "ready");
  const counted = items.filter((it) => it.status !== "error").length;
  return {
    items,
    guest,
    pending: items.some((it) => it.status === "uploading"),
    ready,
    note,
    driveOff,
    room: guest ? max : Math.max(0, max - counted),
    add,
    remove,
    clear,
  };
}
